import { ComponentType, lazy } from 'react';

const lazyReloadFlag = 'pulse:lazy-reload-attempted';

const chunkLoadErrorPattern =
    /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk .* failed/i;

function isChunkLoadError(error: unknown): boolean {
    if (typeof error === 'object' && error !== null && 'message' in error) {
        const message = (error as { message?: unknown }).message;

        return (
            typeof message === 'string' && chunkLoadErrorPattern.test(message)
        );
    }

    return typeof error === 'string' && chunkLoadErrorPattern.test(error);
}

function hasAlreadyReloaded(): boolean {
    try {
        return window.sessionStorage?.getItem(lazyReloadFlag) === 'true';
    } catch {
        return false;
    }
}

function markReloadAttempted(): void {
    try {
        window.sessionStorage?.setItem(lazyReloadFlag, 'true');
    } catch {
        // If session storage is unavailable, reload once for the current page.
    }
}

function clearReloadAttempt(): void {
    try {
        window.sessionStorage?.removeItem(lazyReloadFlag);
    } catch {
        // Storage can be unavailable in privacy modes.
    }
}

export function lazyWithRetry<T extends ComponentType<any>>(
    importer: () => Promise<{ default: T }>
) {
    return lazy(async () => {
        try {
            const component = await importer();
            clearReloadAttempt();

            return component;
        } catch (error) {
            if (isChunkLoadError(error) && !hasAlreadyReloaded()) {
                markReloadAttempted();
                window.location.reload();

                return new Promise<{ default: T }>(() => {});
            }

            throw error;
        }
    });
}
